import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCreateOrganization } from '@fluxter/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthHeader, AuthShell } from '@/components/AuthShell';

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const createOrg = useCreateOrganization();

  const [organizationName, setOrganizationName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The "already has an org → redirect home" guard lives in the route's beforeLoad.

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      const result = await createOrg.mutateAsync({
        organization_name: organizationName.trim(),
        workspace_name: workspaceName.trim(),
      });
      const slug = (result as { data?: { attributes?: { slug?: string } } } | undefined)?.data?.attributes?.slug;
      if (slug) {
        await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug: slug } });
      } else {
        await navigate({ to: '/' });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create organization.');
    }
  };

  return (
    <AuthShell
      width="md"
      header={
        <AuthHeader
          title="Welcome to Fluxter"
          subtitle="Let's set up your organization. You can rename or add more workspaces later."
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="organization_name">Organization name</Label>
          <Input
            id="organization_name"
            type="text"
            required
            autoFocus
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Acme Consulting"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="workspace_name">First workspace name</Label>
          <Input
            id="workspace_name"
            type="text"
            required
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            placeholder="Acme Internal"
          />
          <p className="text-foreground-faint text-[11.5px]">
            A workspace is a silo for projects, tasks, and notes. You can add more later.
          </p>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={createOrg.isPending || !organizationName.trim() || !workspaceName.trim()}
        >
          {createOrg.isPending ? 'Creating…' : 'Create organization'}
        </Button>

        {error ? (
          <p className="text-destructive text-center text-[12.5px]" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </AuthShell>
  );
};
